/**
 * CEO Command Center - Signal Detection Engine
 * 
 * The Signal Engine is the core intelligence layer that identifies items
 * requiring CEO attention. It analyzes tasks, deals, clients, and financial
 * data to surface "signals" - high-priority items that need immediate focus.
 * 
 * Key Concepts:
 * - Signal: An item that requires CEO attention
 * - Urgency Score: 0-100 score based on weighted factors
 * - Signal Level: critical/high/medium/low based on urgency score
 * 
 * The engine uses configurable thresholds defined in config/constants.ts
 * 
 * @module services/signalEngine
 * @version 10.0.0
 */

import type { 
  Task, 
  Deal, 
  Client, 
  CashFlowRecord, 
  Investor,
  Signal,
  SignalLevel,
  BoardReadiness,
} from '../types';

import {
  SIGNAL_THRESHOLDS,
  URGENCY_WEIGHTS,
  SIGNAL_LEVELS,
  BOARD_READINESS_CONFIG,
} from '../config/constants';

import { daysUntil, daysSince, formatCurrency } from '../utils/format';

// ============================================
// TYPES
// ============================================

/**
 * Context data required for signal detection
 */
export interface SignalContext {
  tasks: Task[];
  deals: Deal[];
  clients: Client[];
  cashflow: CashFlowRecord[];
  investors?: Investor[];
  mauData?: Array<{ month: string; mau: number }>;
}

/**
 * Urgency score breakdown for debugging/display
 */
export interface UrgencyBreakdown {
  timeSensitivity: number;
  revenueImpact: number;
  strategicAlignment: number;
  dependencyChain: number;
  total: number;
}

// ============================================
// URGENCY SCORE CALCULATION
// ============================================

/**
 * Calculate the time sensitivity score for an item
 * Higher score = more urgent based on deadline proximity
 * 
 * @param item - Task or Deal with target/close date
 * @returns Score 0-100
 */
function calculateTimeSensitivity(item: Task | Deal): number {
  const dateField = 'targetDate' in item ? item.targetDate : item.closeDate;
  
  let score = 50; // Default for items without dates
  
  if (dateField) {
    const days = daysUntil(dateField);
    
    if (days < 0) score = 100;         // Overdue
    else if (days === 0) score = 95;   // Due today
    else if (days <= 3) score = 85;    // Due within 3 days
    else if (days <= 7) score = 70;    // Due this week
    else if (days <= 14) score = 55;   // Due within 2 weeks
    else score = 25;                    // Due later
  }
  
  // Overdue tasks get high time sensitivity
  if ('targetDate' in item && item.targetDate) {
    const now = new Date();
    const target = new Date(item.targetDate);
    if (target < now && 'status' in item && item.status !== 'Done') {
      score = Math.max(score, 90);
    }
  }
  
  return score;
}

/**
 * Calculate the revenue impact score for an item
 * Higher score = more financial impact at stake
 * 
 * @param item - Task or Deal with revenue data
 * @returns Score 0-100
 */
function calculateRevenueImpact(item: Task | Deal): number {
  let score = 30; // Default for items without revenue data
  
  // Deal TCV scoring
  if ('tcv' in item && item.tcv) {
    if (item.tcv >= SIGNAL_THRESHOLDS.DEAL_SIZE_CRITICAL) {
      score = 100;
    } else if (item.tcv >= SIGNAL_THRESHOLDS.DEAL_SIZE_HIGH) {
      score = 80;
    } else if (item.tcv >= 200000) {
      score = 60;
    } else if (item.tcv >= 100000) {
      score = 40;
    }
  }
  
  // Client ARR scoring (for churn-related items)
  if ('arr' in item && item.arr && item.arr >= SIGNAL_THRESHOLDS.CHURN_RISK_ARR) {
    score = Math.max(score, 75);
  }
  
  return score;
}

/**
 * Calculate the strategic alignment score for an item
 * Higher score = more aligned with CEO priorities
 * 
 * @param item - Task or Deal
 * @returns Score 0-100
 */
function calculateStrategicAlignment(item: Task | Deal): number {
  let score = 40; // Default
  
  // Items marked as signals are CEO priorities
  if ('isSignal' in item && item.isSignal) {
    score = 90;
  }
  
  // EMG domain items are executive-level
  if ('domain' in item && item.domain === 'EMG') {
    score = Math.max(score, 80);
  }
  
  // High priority tasks
  if ('priority' in item) {
    if (item.priority === 'Critical') score = Math.max(score, 85);
    else if (item.priority === 'High') score = Math.max(score, 70);
  }
  
  // Advanced deal stages
  if ('stage' in item) {
    if (item.stage === 'Contracting') score = Math.max(score, 75);
    else if (item.stage === 'POC') score = Math.max(score, 65);
  }
  
  return score;
}

/**
 * Calculate the dependency chain score for an item
 * Higher score = more blocking impact on other work
 * 
 * @param item - Task or Deal
 * @param allTasks - All tasks for context
 * @returns Score 0-100
 */
function calculateDependencyChain(item: Task | Deal, allTasks: Task[]): number {
  let score = 20; // Default
  
  // Overdue tasks are higher priority
  if ('targetDate' in item && item.targetDate && 'status' in item && item.status !== 'Done') {
    const now = new Date();
    const target = new Date(item.targetDate);
    if (target < now) {
      score = 75;
    }
  }
  
  // Check owner workload - if owner has many open items, this one is more critical
  if ('owner' in item && item.owner) {
    const ownerTasks = allTasks.filter(
      t => t.owner === item.owner && t.status !== 'Done'
    );
    if (ownerTasks.length > 5) {
      score = Math.max(score, 50);
    }
    if (ownerTasks.length > 10) {
      score = Math.max(score, 65);
    }
  }
  
  return score;
}

/**
 * Calculate the overall urgency score for an item
 * Combines all factors using configured weights
 * 
 * @param item - Task or Deal to score
 * @param context - Full context data
 * @returns Urgency score 0-100
 * 
 * @example
 * const score = calculateUrgencyScore(task, { tasks, deals, clients, cashflow });
 * // Returns: 78
 */
export function calculateUrgencyScore(
  item: Task | Deal,
  context: SignalContext
): number {
  const timeSensitivity = calculateTimeSensitivity(item);
  const revenueImpact = calculateRevenueImpact(item);
  const strategicAlignment = calculateStrategicAlignment(item);
  const dependencyChain = calculateDependencyChain(item, context.tasks);
  
  const score = 
    timeSensitivity * URGENCY_WEIGHTS.TIME_SENSITIVITY +
    revenueImpact * URGENCY_WEIGHTS.REVENUE_IMPACT +
    strategicAlignment * URGENCY_WEIGHTS.STRATEGIC_ALIGNMENT +
    dependencyChain * URGENCY_WEIGHTS.DEPENDENCY_CHAIN;
  
  // Clamp to 0-100 range
  return Math.round(Math.min(Math.max(score, 0), 100));
}

/**
 * Get detailed breakdown of urgency score
 * Useful for debugging and transparency
 * 
 * @param item - Task or Deal to analyze
 * @param context - Full context data
 * @returns Breakdown of score components
 */
export function getUrgencyBreakdown(
  item: Task | Deal,
  context: SignalContext
): UrgencyBreakdown {
  const timeSensitivity = calculateTimeSensitivity(item);
  const revenueImpact = calculateRevenueImpact(item);
  const strategicAlignment = calculateStrategicAlignment(item);
  const dependencyChain = calculateDependencyChain(item, context.tasks);
  
  return {
    timeSensitivity,
    revenueImpact,
    strategicAlignment,
    dependencyChain,
    total: calculateUrgencyScore(item, context),
  };
}

/**
 * Get the signal level based on urgency score
 * 
 * @param score - Urgency score 0-100
 * @returns Signal level
 */
export function getSignalLevel(score: number): SignalLevel {
  if (score >= SIGNAL_LEVELS.CRITICAL) return 'critical';
  if (score >= SIGNAL_LEVELS.HIGH) return 'high';
  if (score >= SIGNAL_LEVELS.MEDIUM) return 'medium';
  return 'low';
}

// ============================================
// SIGNAL DETECTION
// ============================================

/**
 * Detect deal-related signals
 */
function detectDealSignals(deals: Deal[], context: SignalContext): Signal[] {
  const signals: Signal[] = [];
  
  deals.forEach(deal => {
    // Skip closed deals
    if (deal.stage === 'Closed Won' || deal.stage === 'Closed Lost') {
      return;
    }
    
    const urgencyScore = calculateUrgencyScore(deal, context);
    
    // Critical deal size signals
    if (deal.tcv >= SIGNAL_THRESHOLDS.DEAL_SIZE_CRITICAL) {
      signals.push({
        id: `deal-critical-${deal.id}`,
        type: 'deal',
        level: 'critical',
        title: `${formatCurrency(deal.tcv)} deal: ${deal.prospect}`,
        description: `High-value deal in ${deal.stage} stage`,
        source: deal,
        urgencyScore,
        category: 'revenue',
        value: deal.tcv,
        daysLeft: deal.closeDate ? daysUntil(deal.closeDate) : undefined,
      });
    }
    // High deal size signals
    else if (deal.tcv >= SIGNAL_THRESHOLDS.DEAL_SIZE_HIGH) {
      signals.push({
        id: `deal-high-${deal.id}`,
        type: 'deal',
        level: 'high',
        title: `${formatCurrency(deal.tcv)} deal: ${deal.prospect}`,
        description: `Large deal closing ${deal.closeDate ? `in ${daysUntil(deal.closeDate)} days` : 'TBD'}`,
        source: deal,
        urgencyScore,
        category: 'revenue',
        value: deal.tcv,
        daysLeft: deal.closeDate ? daysUntil(deal.closeDate) : undefined,
      });
    }
    
    // Stuck deal signals (no progress in 30+ days)
    if (deal.updatedAt) {
      const stuckDays = daysSince(deal.updatedAt);
      if (stuckDays >= SIGNAL_THRESHOLDS.DEAL_STUCK_DAYS && deal.tcv >= 100000) {
        signals.push({
          id: `deal-stuck-${deal.id}`,
          type: 'deal',
          level: 'medium',
          title: `Stalled: ${deal.prospect}`,
          description: `No progress for ${stuckDays} days | ${formatCurrency(deal.tcv)}`,
          source: deal,
          urgencyScore: Math.min(urgencyScore + 10, 100),
          category: 'revenue',
          value: deal.tcv,
        });
      }
    }
  });
  
  return signals;
}

/**
 * Detect client churn risk signals
 */
function detectChurnSignals(clients: Client[]): Signal[] {
  const signals: Signal[] = [];
  
  clients.forEach(client => {
    // Skip churned clients
    if (client.status === 'Churned') return;
    
    // Contract expiry signals
    if (client.contractEnd) {
      const daysToExpiry = daysUntil(client.contractEnd);
      const arr = client.arr || client.acv2026 || 0;
      
      if (daysToExpiry <= 30 && daysToExpiry > 0 && arr >= SIGNAL_THRESHOLDS.CHURN_RISK_ARR) {
        const level: SignalLevel = daysToExpiry <= SIGNAL_THRESHOLDS.CHURN_DAYS_CRITICAL 
          ? 'critical' 
          : 'high';
        
        signals.push({
          id: `client-expiry-${client.id}`,
          type: 'client',
          level,
          title: `Contract expiring: ${client.name}`,
          description: `${formatCurrency(arr)} ARR expires in ${daysToExpiry} days`,
          source: client,
          urgencyScore: 90 - daysToExpiry,
          category: 'retention',
          value: arr,
          daysLeft: daysToExpiry,
        });
      }
    }
    
    // Low health score signals
    if (client.healthScore !== undefined && client.healthScore < 50) {
      const arr = client.arr || client.acv2026 || 0;
      if (arr >= SIGNAL_THRESHOLDS.CHURN_RISK_ARR) {
        signals.push({
          id: `client-health-${client.id}`,
          type: 'client',
          level: client.healthScore < 30 ? 'critical' : 'high',
          title: `At-risk client: ${client.name}`,
          description: `Health score ${client.healthScore}% | ${formatCurrency(arr)} ARR`,
          source: client,
          urgencyScore: 100 - client.healthScore,
          category: 'retention',
          value: arr,
        });
      }
    }
  });
  
  return signals;
}

/**
 * Detect cash runway signals
 */
function detectCashSignals(cashflow: CashFlowRecord[]): Signal[] {
  const signals: Signal[] = [];
  
  if (!cashflow || cashflow.length === 0) {
    return signals;
  }
  
  // Sort by date descending to get most recent
  const sorted = [...cashflow].sort(
    (a, b) => new Date(b.monthDate).getTime() - new Date(a.monthDate).getTime()
  );

  // Helper functions for revenue and expenses
  const getRevenue = (cf: CashFlowRecord) => (cf.recurringRevenue || 0) + (cf.newDealRevenue || 0);
  const getExpenses = (cf: CashFlowRecord) =>
    (cf.personnelCost || 0) + (cf.infrastructureCost || 0) +
    (cf.marketingCost || 0) + (cf.legalCost || 0) + (cf.otherCost || 0);

  // Get current cash from current month or most recent actual (non-forecast)
  const today = new Date();
  const currentMonthStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  const actualMonths = sorted.filter(cf => !cf.isForecast);
  const currentMonthData = sorted.find(cf => cf.monthDate.startsWith(currentMonthStr))
    || actualMonths[0]
    || sorted[0];
  const currentCash = currentMonthData?.balance || 250000;

  // Calculate average monthly burn from last 6 actual months
  const recentMonths = actualMonths.slice(0, 6);
  const avgBurn = recentMonths.length > 0
    ? recentMonths.reduce((sum, cf) => sum + (getExpenses(cf) - getRevenue(cf)), 0) / recentMonths.length
    : 0;
  
  // Calculate runway in months
  const runwayMonths = avgBurn > 0 ? Math.floor(currentCash / avgBurn) : 99;
  
  // Critical runway signal
  if (runwayMonths <= SIGNAL_THRESHOLDS.CASH_RUNWAY_CRITICAL) {
    signals.push({
      id: 'cash-runway-critical',
      type: 'finance',
      level: 'critical',
      title: `Critical: ${runwayMonths} months runway`,
      description: `Current cash: ${formatCurrency(currentCash)}`,
      urgencyScore: 100,
      category: 'finance',
      value: currentCash,
      daysLeft: runwayMonths * 30,
    });
  }
  // Warning runway signal
  else if (runwayMonths <= SIGNAL_THRESHOLDS.CASH_RUNWAY_WARNING) {
    signals.push({
      id: 'cash-runway-warning',
      type: 'finance',
      level: 'high',
      title: `Warning: ${runwayMonths} months runway`,
      description: `Accelerate fundraising or reduce burn`,
      urgencyScore: 85,
      category: 'finance',
      value: currentCash,
      daysLeft: runwayMonths * 30,
    });
  }
  
  return signals;
}

/**
 * Detect task-related signals (overdue and manually marked)
 */
function detectTaskSignals(tasks: Task[], context: SignalContext): Signal[] {
  const signals: Signal[] = [];
  const processedIds = new Set<string>();

  // Debug: Log all tasks with isSignal flag
  const signalTasks = tasks.filter(t => t.isSignal);
  if (signalTasks.length > 0) {
    console.log('[SignalEngine] Tasks marked as signals:', signalTasks.map(t => ({ id: t.id, title: t.title, isSignal: t.isSignal, status: t.status })));
  }

  tasks.forEach(task => {
    // Skip completed tasks
    if (task.status === 'Done') return;

    const urgencyScore = calculateUrgencyScore(task, context);

    // CEO-marked signals (isSignal: true) - always show these
    if (task.isSignal && !processedIds.has(task.id)) {
      console.log('[SignalEngine] Creating signal for task:', task.id, task.title);
      processedIds.add(task.id);
      const level: SignalLevel = task.priority === 'Critical' ? 'critical'
        : task.priority === 'High' ? 'high'
        : 'medium';
      signals.push({
        id: `task-signal-${task.id}`,
        type: 'task',
        level,
        title: task.title,
        description: `CEO Priority | ${task.priority} | ${task.domain || 'General'}`,
        source: task,
        urgencyScore: Math.max(urgencyScore, 70), // Ensure minimum visibility
        category: 'execution',
      });
    }

    // Overdue task signals (only High/Critical priority)
    if (task.targetDate && (task.priority === 'Critical' || task.priority === 'High') && !processedIds.has(task.id)) {
      const daysOverdue = -daysUntil(task.targetDate);

      if (daysOverdue >= Math.abs(SIGNAL_THRESHOLDS.OVERDUE_CRITICAL)) {
        processedIds.add(task.id);
        signals.push({
          id: `task-overdue-${task.id}`,
          type: 'task',
          level: daysOverdue >= 14 ? 'high' : 'medium',
          title: `Overdue: ${task.title}`,
          description: `${daysOverdue} days overdue | ${task.priority} priority`,
          source: task,
          urgencyScore,
          category: 'execution',
        });
      }
    }
  });

  return signals;
}

/**
 * Detect investor follow-up signals
 */
function detectInvestorSignals(investors: Investor[]): Signal[] {
  const signals: Signal[] = [];
  
  if (!investors) return signals;
  
  investors.forEach(investor => {
    // Skip closed/passed
    if (investor.stage === 'Closed' || investor.stage === 'Passed') return;
    
    // Skip research stage (no urgency yet)
    if (investor.stage === 'Research') return;
    
    if (investor.lastContact) {
      const daysSinceContact = daysSince(investor.lastContact);
      
      if (daysSinceContact >= SIGNAL_THRESHOLDS.INVESTOR_FOLLOWUP) {
        const level: SignalLevel = daysSinceContact >= SIGNAL_THRESHOLDS.INVESTOR_FOLLOWUP_URGENT 
          ? 'high' 
          : 'medium';
        
        signals.push({
          id: `investor-followup-${investor.id}`,
          type: 'investor',
          level,
          title: `Follow up: ${investor.name}`,
          description: `${daysSinceContact} days since contact | Stage: ${investor.stage}`,
          source: investor,
          urgencyScore: 60 + daysSinceContact,
          category: 'fundraising',
        });
      }
    }
  });
  
  return signals;
}

/**
 * Detect all signals across all data sources
 * This is the main entry point for signal detection
 * 
 * @param context - All data context
 * @returns Array of signals sorted by urgency
 * 
 * @example
 * const signals = detectSignals({
 *   tasks,
 *   deals,
 *   clients,
 *   cashflow,
 *   investors
 * });
 */
export function detectSignals(context: SignalContext): Signal[] {
  const signals: Signal[] = [];
  
  // Detect signals from each source
  signals.push(...detectDealSignals(context.deals, context));
  signals.push(...detectChurnSignals(context.clients));
  signals.push(...detectCashSignals(context.cashflow));
  signals.push(...detectTaskSignals(context.tasks, context));
  
  if (context.investors) {
    signals.push(...detectInvestorSignals(context.investors));
  }
  
  // Sort by urgency score (highest first)
  return signals.sort((a, b) => b.urgencyScore - a.urgencyScore);
}

/**
 * Get top N signals
 * 
 * @param context - All data context
 * @param n - Number of signals to return
 * @returns Top N signals by urgency
 */
export function getTopSignals(context: SignalContext, n: number = 3): Signal[] {
  return detectSignals(context).slice(0, n);
}

/**
 * Get signals grouped by level
 * 
 * @param context - All data context
 * @returns Signals grouped by level
 */
export function getSignalsByLevel(context: SignalContext): Record<SignalLevel, Signal[]> {
  const signals = detectSignals(context);
  
  return {
    critical: signals.filter(s => s.level === 'critical'),
    high: signals.filter(s => s.level === 'high'),
    medium: signals.filter(s => s.level === 'medium'),
    low: signals.filter(s => s.level === 'low'),
  };
}

/**
 * Get signal counts by level
 */
export function getSignalCounts(context: SignalContext): Record<SignalLevel | 'total', number> {
  const signals = detectSignals(context);
  
  return {
    critical: signals.filter(s => s.level === 'critical').length,
    high: signals.filter(s => s.level === 'high').length,
    medium: signals.filter(s => s.level === 'medium').length,
    low: signals.filter(s => s.level === 'low').length,
    total: signals.length,
  };
}

// ============================================
// BOARD READINESS CALCULATION
// ============================================

/**
 * Calculate board readiness score
 * Evaluates data completeness and business health
 * 
 * @param context - All data context
 * @returns Board readiness assessment
 * 
 * @example
 * const readiness = calculateBoardReadiness({
 *   tasks, deals, clients, cashflow, mauData
 * });
 * // Returns: { score: 75, status: 'needs-attention', issues: [...] }
 */
export function calculateBoardReadiness(context: SignalContext): BoardReadiness {
  const { tasks, deals, clients, cashflow, mauData } = context;
  const { PENALTIES, STATUS_THRESHOLDS, DATA_FRESHNESS } = BOARD_READINESS_CONFIG;
  
  let score = 100;
  const issues: string[] = [];
  
  // Check cash flow data freshness
  if (!cashflow || cashflow.length < DATA_FRESHNESS.CASHFLOW_MONTHS) {
    score -= PENALTIES.MISSING_CASHFLOW;
    issues.push('Missing recent cash flow data');
  }
  
  // Check pipeline health
  const activeDeals = deals?.filter(
    d => !['Closed Won', 'Closed Lost'].includes(d.stage)
  ) || [];
  
  if (activeDeals.length === 0) {
    score -= PENALTIES.NO_ACTIVE_DEALS;
    issues.push('No active deals in pipeline');
  } else {
    const weightedPipeline = activeDeals.reduce(
      (sum, d) => sum + ((d.tcv || 0) * (d.probability || 0)),
      0
    );
    if (weightedPipeline < 1_000_000) {
      score -= PENALTIES.LOW_PIPELINE;
      issues.push('Pipeline below €1M weighted');
    }
  }
  
  // Check MAU data freshness
  if (!mauData || mauData.length < DATA_FRESHNESS.MAU_MONTHS) {
    score -= PENALTIES.OUTDATED_MAU;
    issues.push('MAU data needs update');
  }
  
  // Check overdue tasks
  const overdueTasks = tasks?.filter(
    t => t.status !== 'Done' && daysUntil(t.targetDate) < 0
  ) || [];
  if (overdueTasks.length > 10) {
    score -= PENALTIES.TOO_MANY_OVERDUE;
    issues.push(`${overdueTasks.length} overdue tasks`);
  }
  
  // Check unassigned tasks
  const unassignedTasks = tasks?.filter(
    t => !t.owner && t.status !== 'Done'
  ) || [];
  if (unassignedTasks.length > 5) {
    score -= PENALTIES.TASKS_NO_OWNER;
    issues.push('Tasks missing owners');
  }
  
  // Check at-risk clients
  const atRiskClients = clients?.filter(
    c => c.healthScore !== undefined && c.healthScore < 50
  ) || [];
  if (atRiskClients.length > 2) {
    score -= PENALTIES.AT_RISK_CLIENTS;
    issues.push(`${atRiskClients.length} at-risk clients`);
  }
  
  // Determine status
  const finalScore = Math.max(0, score);
  let status: BoardReadiness['status'];
  
  if (finalScore >= STATUS_THRESHOLDS.READY) {
    status = 'ready';
  } else if (finalScore >= STATUS_THRESHOLDS.NEEDS_ATTENTION) {
    status = 'needs-attention';
  } else {
    status = 'not-ready';
  }
  
  return {
    score: finalScore,
    status,
    issues,
    assessedAt: new Date().toISOString(),
  };
}

// ============================================
// EXPORT
// ============================================

export const SignalEngine = {
  calculateUrgencyScore,
  getUrgencyBreakdown,
  getSignalLevel,
  detectSignals,
  getTopSignals,
  getSignalsByLevel,
  getSignalCounts,
  calculateBoardReadiness,
};

export default SignalEngine;
